/**
 * LegacyAdapter - Bridge between old and new architecture
 * 
 * This module provides compatibility with the existing v4.x code
 * during the gradual migration to v5.0
 * 
 * @module legacy/LegacyAdapter
 */

import AppState from '../core/AppState.js';
import { actions as storeActions, dispatch as storeDispatch } from '../store/index.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import { ModalService } from '../ui-kit/ModalService.jsx';

class LegacyAdapterClass {
  constructor() {
    this.initialized = false;
  }
  
  /**
   * Initialize the legacy adapter
   */
  async initialize() {
    console.log('🔄 Initializing legacy adapter...');
    
    // Set up global compatibility functions
    this.setupGlobalFunctions();
    
    // Set up global variables
    this.setupGlobalVariables();
    
    // Migrate existing data
    await this.migrateData();
    
    this.initialized = true;
    console.log('✅ Legacy adapter ready');
  }
  
  /**
   * Set up global functions for backward compatibility
   */
  setupGlobalFunctions() {
    // Toast function
    if (!window.showToast) {
      window.showToast = (message, type = 'info') => {
        EventBus.emit(AppEvents.TOAST_SHOW, { message, type });
      };
    }
    
    // Save functions
    window.saveToLocalStorage = () => {
      AppState.persistState();
    };
    
    // Display functions
    window.displayDatabase = () => {
      console.log('Legacy displayDatabase called - handled by InitiativesModule');
    };
    
    window.updateStats = () => {
      console.log('Legacy updateStats called - handled by AppState');
    };
    
    // Settings functions
    window.showSettings = () => {
      try {
        this._legacySettingsClose = ModalService.open({
          title: 'Settings',
          content: '<p>Settings interface coming soon</p>',
          actions: [{ text: 'Close', class: 'uk-btn uk-btn-secondary', action: () => {} }]
        });
      } catch (_) {}
    };
    window.closeSettings = () => {
      try { this._legacySettingsClose?.(); } catch (_) {}
      this._legacySettingsClose = null;
    };
    
    // Initiative functions
    window.saveGoal = (data) => {
      AppState.addInitiative(data);
    };
    
    window.deleteGoal = (id) => {
      AppState.deleteInitiative(id);
    };
    
    // Chat functions
    window.sendMessage = () => {
      EventBus.emit('legacy:sendMessage');
    };
    
    window.addChatMessage = (content, sender) => {
      AppState.addChatMessage({ content, sender });
    };
    
    console.log('✅ Global compatibility functions installed');
  }
  
  /**
   * Set up global variables for backward compatibility
   */
  setupGlobalVariables() {
    // Define getters that map to AppState
    Object.defineProperty(window, 'goalsDatabase', {
      get: () => AppState.getState('initiatives') || [],
      set: (value) => { try { storeDispatch(storeActions.setInitiatives(value)); } catch (_) { AppState.setState({ initiatives: value }); } }
    });
    
    Object.defineProperty(window, 'currentStrategy', {
      get: () => AppState.getState('strategies.current'),
      set: (value) => { try { storeDispatch(storeActions.setCurrentStrategy(value)); } catch (_) { AppState.setState({ strategies: { current: value } }); } }
    });
    
    Object.defineProperty(window, 'uploadedStrategy', {
      get: () => AppState.getState('strategies.uploaded'),
      set: (value) => { try { storeDispatch(storeActions.setUploadedStrategy(value)); } catch (_) { AppState.setState({ strategies: { uploaded: value } }); } }
    });
    
    Object.defineProperty(window, 'emergentStrategy', {
      get: () => AppState.getState('strategies.emergent'),
      set: (value) => { try { storeDispatch(storeActions.setEmergentStrategy(value)); } catch (_) { AppState.setState({ strategies: { emergent: value } }); } }
    });
    
    Object.defineProperty(window, 'chatHistory', {
      get: () => AppState.getState('chat.messages') || [],
      set: (value) => AppState.setState({ chat: { messages: value } })
    });
    
    // Static variables
    window.selectedType = 'Goal';
    window.databaseCollapsed = false;
    window.conversationDepth = 0;
    window.isEmergentStrategy = false;
    
    // Config reference
    window.config = {
      defaultStrategy: {
        name: 'Default Strategy',
        pillars: []
      },
      initiativeTypes: [
        { value: 'Goal', color: '#28a745' },
        { value: 'Challenge', color: '#dc3545' },
        { value: 'Demand', color: '#ffc107' },
        { value: 'Initiative', color: '#005EB8' }
      ],
      ui: {
        toastDuration: 3000
      },
      fileUpload: {
        maxSize: 10 * 1024 * 1024
      }
    };
    
    console.log('✅ Global compatibility variables installed');
  }
  
  /**
   * Migrate existing data to new structure
   */
  async migrateData() {
    try {
      // Migrate initiatives
      const oldGoals = localStorage.getItem('goalsDatabase');
      if (oldGoals) {
        const goals = JSON.parse(oldGoals);
        try { storeDispatch(storeActions.setInitiatives(goals)); } catch (_) { AppState.setState({ initiatives: goals }); }
        console.log(`✅ Migrated ${goals.length} initiatives`);
      }
      
      // Migrate strategies
      const uploadedStrategy = localStorage.getItem('uploadedStrategy');
      const emergentStrategy = localStorage.getItem('emergentStrategy');
      const companyStrategy = localStorage.getItem('companyStrategy');
      
      if (uploadedStrategy || emergentStrategy || companyStrategy) {
        const u = uploadedStrategy ? JSON.parse(uploadedStrategy) : (companyStrategy ? JSON.parse(companyStrategy) : null);
        const e = emergentStrategy ? JSON.parse(emergentStrategy) : null;
        try {
          if (u !== null) storeDispatch(storeActions.setUploadedStrategy(u));
          if (e !== null) storeDispatch(storeActions.setEmergentStrategy(e));
        } catch (_) {
          AppState.setState({ strategies: { uploaded: u, emergent: e } });
        }
        console.log('✅ Migrated strategies');
      }
      
      // Migrate API key
      const apiKey = localStorage.getItem('openai_api_key');
      if (apiKey) {
        try { storeDispatch(storeActions.setApiKey(apiKey)); } catch (_) { AppState.setState({ user: { apiKey } }); }
        console.log('✅ Migrated API configuration');
      }
      
    } catch (error) {
      console.error('Migration error:', error);
    }
  }
  
  /**
   * Check if legacy code is trying to run
   */
  interceptLegacyCode() {
    // Monitor for legacy script errors
    window.addEventListener('error', (event) => {
      if (event.message.includes('is not defined')) {
        console.warn('Legacy code error intercepted:', event.message);
        // Try to handle gracefully
        return true; // Prevent default error handling
      }
    });
  }
}

// Create singleton instance
export const LegacyAdapter = new LegacyAdapterClass();

// Attach to window for debugging
if (import.meta.env.DEV) {
  window.LegacyAdapter = LegacyAdapter;
  console.log('🔧 LegacyAdapter available at window.LegacyAdapter');
}
